import React, {ChangeEvent, useCallback, useEffect, useRef, useState} from 'react';
import {Button, message} from 'antd';
import OrderListingQueryHint from '../../../../components/filters/OrderListingQueryHint';

import {useParcelDownload} from "../../../../hooks/useParcelDownload";
import ParcelsDownloadModal from "./ParcelsDownloadModal";
import {MARK_AS_EXCEPTION} from '../../../../graphql/mutations/markAsException';
import {useMutation, useQuery} from '@apollo/client';
import dayjs from 'dayjs';
import {
    BATCH_MARK_FOR_MANUAL_REVIEW,
    BatchMarkForManualReviewResponse,
    BatchMarkForManualReviewVariables
} from '../graphql/mutations/batchMarkForManualReviewMutations';
import AddToPodReviewModal from './AddToPodReviewModal';
import {getUserInfo} from "../../../../utils/auth";
import {OrderType} from "../../../../graphql/types/OrderType";
import {
    GENERATE_BATCH_SHIPPING_LABEL_PDF,
    GenerateBatchShippingLabelPdfResponse,
    GenerateBatchShippingLabelPdfVariables
} from "../../../delivery/parcels/graphql/mutations/generateShippingLabelPdf";
import {allChannelResponse, GET_ALL_CHANNEL} from '../../../../graphql/queries/getChannelAll';
import {printPdfFromBase64} from "../../../wms/bags/utils/printUtils";
import InputFilterItem from "../../../delivery/components/InputFilterItem";
import BatchInputFilterItem from "../../../delivery/components/BatchInputFilterItem";
import ParcelFilterStyle from "../../../delivery/components/ParcelFilterStyle";
import DateTimeRangeFilterItem from "../../../delivery/components/DateTimeRangeFilterItem";
import BagTaskNumberFilterItem from '../../../central/parcels/components/BagTaskNumberFilterItem';
import {
    hasExceptionOptions,
    isComplaintOptions,
    podReviewStatusOptions
} from "../../../../config/filterOptions";
import {OrderFiltersType} from "../../../../graphql/types/OrderFiltersType";
import {
    ExceptionDictionaryResponse,
    ExceptionDictionaryType,
    GET_ALL_EXCEPTION
} from "../../../delivery/parcels/graphql/queries/getAllException";
import {filterExceptionByUserRole} from "../../../../config/roleExceptionConfig";
import SetReturnStatusModal, {SetReturnStatusData} from "../../../../components/SetReturnStatusModal";
import {
    GET_NEXT_ACTION_LIST,
    NextActionListResponse,
    NextActionListType
} from "../../../../graphql/queries/getNextActionList";
import {SET_RETURNED_STATUS} from "../../../../graphql/mutations/setReturnedStatus";
import {
    GENERATE_PARCEL_DOWNLOAD_REPORT,
    GenerateParcelDownloadReportResponse,
    GenerateParcelDownloadReportVariables
} from "../graphql/mutations/generateParcelDownloadReport";
import {formatDateTimeRangeToISO, getLastWeekDateTimeRange} from "../../../../utils/dateTimeUtils";
import { parseException, showError } from '../../../../utils/exceptionUtils';
import {GET_HUB_LIST_FOR_OPTIONS, GetHubListForOptionResponse} from "../../../../graphql/queries/warehouseQueries";
import BatchMarkAsExceptionModal, {BatchMarkExceptionInput} from 'components/BatchMarkAsExceptionModal';
import {useTranslation} from 'react-i18next';
import {useStatusInternationalization} from 'pages/delivery/parcels/hooks/useStatusInternationalization';

import {HubSelectFilter} from '../../../../components/filters/HubSelectFilter';
import {WarehouseSelectFilter} from '../../../../components/filters/WarehouseSelectFilter';
import { PrePickupHubSelectFilter } from '../../../../components/filters/PrePickupHubSelectFilter';
import {DspSelectFilter} from '../../../../components/filters/DspSelectFilter';
import {MultiClientSelectFilter} from '../../../../components/filters/MultiClientSelectFilter';
import {MultiPickupHubSelectFilter} from '../../../../components/filters/MultiPickupHubSelectFilter';
import {MultiParcelStatusSelectFilter} from '../../../../components/filters/MultiParcelStatusSelectFilter';
import {WarehousesResponse} from "../../../../graphql/queries/getWarehouseForHeadquarters";
import {GET_WAREHOUSES_FOR_CENTRAL} from "../../../central/exceptions/graphql/queries/getWarehouseForCentral";
import {SiteType} from "../../../../graphql/types/SiteType";
import {useWarehouseContext} from "../../../../contexts/WarehouseContext";
import { getSmartLabelClassName } from '../../../../utils/labelWidthUtils';
import { useAutoClearDefaultTime } from '../../../../hooks/useAutoClearDefaultTime';

interface CentralParcelFilterProps {
    filters: OrderFiltersType;
    setFilters: (filters: OrderFiltersType | ((prev: OrderFiltersType) => OrderFiltersType)) => void;
    handleFilter: () => void;
    handleResetFilters: () => void;
    totalElements: number;
    selectedRows: OrderType[];
    onClearSelection?: () => void; // 添加清空选择的回调函数
}

interface CustomerOption {
    value: string;
    label: string;
}

const CentralParcelFilter: React.FC<CentralParcelFilterProps> = ({
                                                       filters,
                                                       setFilters,
                                                       handleFilter,
                                                       handleResetFilters,
                                                       totalElements,
                                                       selectedRows,
                                                       onClearSelection,
                                                   }) => {
    const {t} = useTranslation();
    const PICKUP_PARCEL_DOWNLOAD_MAX_COUNT = 20000;

    // ===== Label constants (for cross-block column alignment) =====
    const labelTracking = String(t('central.parcels.filters.tracking'));
    const labelOrder = String(t('central.parcels.filters.order'));
    const labelClient = String(t('central.parcels.filters.client'));
    const labelParcelStatus = String(t('central.parcels.filters.parcelStatus'));
    const labelDsp = 'DSP';

    const labelSortCode1 = String(t('central.parcels.filters.sortCode1'));
    const labelSortCode2 = String(t('central.parcels.filters.sortCode2'));
    const labelSortCode3 = String(t('central.parcels.filters.sortCode3'));
    const labelReceiverZipcode = String(t('central.parcels.filters.receiverZipcode'));
    const labelPickupHub = String(t('delivery.parcels.label.pickupHub'));
    const labelPickupSite = String(t('central.parcels.filters.pickupSite'));
    const labelBagTaskNumber = String(t('central.parcels.filters.bagTaskNumber'));
    const labelSortingChannelOrderNumber = String(t('central.parcels.filters.sortingChannelOrderNumber'));
    const labelPrePickupHub = String(t('central.parcels.filters.prePickupHub'));

    const labelOrderTime = String(t('central.parcels.filters.orderTime'));
    const labelPickupTaskDate = String(t('central.parcels.filters.pickupTaskDate'));
    const labelDeliveredAt = String(t('central.parcels.filters.deliveredAt'));
    const labelHubReceiveDate = String(t('central.parcels.filters.hubReceiveDate'));
    const labelWhsReceiveDate = String(t('central.parcels.filters.whsReceiveDate'));
    const labelLastDeliveryScanTime = String(t('central.parcels.filters.lastDeliveryScanTime'));
    const labelLastTaskDate = String(t('central.parcels.filters.lastTaskDate'));
    const labelLastUpdatedAt = String(t('delivery.parcels.label.lastUpdatedAt'));
    const labelReturnArrivalHubAt = String(t('central.parcels.filters.returnArrivalHubAt'));

    // 默认时间范围不写入 URL：用于 UI 默认展示与下载参数构造
    // - undefined：代表“未初始化”，默认展示最近一周
    // - null：代表用户手动清空（点击叉号），必须保持为空，不再回填默认值
    // 一旦用户动过时间（包含点叉号清空），在 reset/刷新前都不再自动清空默认时间
    const hasUserEditedOrderTimeRef = useRef(false);
    useAutoClearDefaultTime({
        filters,
        setFilters: (next) => setFilters(next),
        timeKey: 'orderTime',
        hasUserEditedTimeRef: hasUserEditedOrderTimeRef,
        // pickup/parcels 页面会在 mount 后通过 useEffect 自动补默认站点/仓库等非时间筛选，
        // 避免因此把默认时间立刻清空
        skipFirstNonTimeChange: true,
        // 揽收中心（pickupHubId）持续跟随左侧仓库切换同步，不属于用户主动改筛选条件，
        // 不应触发默认订单时间被清空
        excludeKeys: ['pickupHubId'],
    });

    const getEffectiveOrderTime = useCallback((
        orderTime: OrderFiltersType['orderTime']
    ): [string | null, string | null] | null => {
        if (orderTime === null) return null;
        const isValid = orderTime &&
            Array.isArray(orderTime) &&
            orderTime.length === 2;
        return isValid ? (orderTime as [string | null, string | null]) : getLastWeekDateTimeRange();
    }, []);

    const labelIsComplaint = String(t('central.parcels.filters.isComplaint'));
    const labelPodReviewStatus = String(t('delivery.parcels.label.podReviewStatus'));
    const labelHasException = String(t('central.parcels.filters.hasException'));
    const labelLastExceptionReason = String(t('central.parcels.filters.lastExceptionReason'));
    const labelLastExceptionTime = String(t('central.parcels.filters.lastExceptionDate'));
    const labelNextAction = String(t('central.parcels.filters.nextAction'));
    const labelExceptionStatus = String(t('central.parcels.filters.exceptionStatus'));

    // ===== Cross-block 3-column alignment =====
    const col1Labels = [
        labelTracking,
        labelOrder,
        labelDsp,
        labelSortCode1,
        labelReceiverZipcode,
        labelPrePickupHub,
        labelOrderTime,
        labelHubReceiveDate,
        labelLastTaskDate,
        labelIsComplaint,
        labelLastExceptionReason,
        labelExceptionStatus,
    ];

    const col2Labels = [
        labelClient,
        labelSortCode2,
        labelPickupHub,
        labelBagTaskNumber,
        labelPickupTaskDate,
        labelWhsReceiveDate,
        labelLastUpdatedAt,
        labelPodReviewStatus,
        labelSortingChannelOrderNumber,
    ];

    const col3Labels = [
        labelParcelStatus,
        labelSortCode3,
        labelPickupSite,
        labelDeliveredAt,
        labelLastDeliveryScanTime,
        labelHasException,
        labelNextAction,
    ];

    // Date Information 区域为二分布局（两列）
    const dateCol1Labels = [
        labelOrderTime,
        labelDeliveredAt,
        labelWhsReceiveDate,
        labelLastTaskDate,
        labelReturnArrivalHubAt,
        labelLastExceptionTime,
    ];
    const dateCol2Labels = [
        labelPickupTaskDate,
        labelHubReceiveDate,
        labelLastDeliveryScanTime,
        labelLastUpdatedAt,
    ];
    // 二分布局的第一列需要与三分布局的第一列对齐：共享同一个 label 宽度计算结果
    const col1SpanClassName = getSmartLabelClassName([...col1Labels, ...dateCol1Labels]);
    const [batchModalVisible, setBatchModalVisible] = useState(false);
    const [batchMarkLoading, setBatchMarkLoading] = useState(false);
    const [returnStatusVisible, setReturnStatusVisible] = useState(false);
    const [returnStatusLoading, setReturnStatusLoading] = useState(false);
    const [addToPodReviewModalVisible, setAddToPodReviewModalVisible] = useState(false);
    const [addToPodReviewResult, setAddToPodReviewResult] = useState<{
        success: boolean;
        markedCount: number;
        failedCount: number;
        failedTrackingNumbers: string[];
    } | null>(null);
    const [addToPodReviewLoading, setAddToPodReviewLoading] = useState(false);
    const [markAsException] = useMutation(MARK_AS_EXCEPTION);
    const [setReturnedStatus] = useMutation(SET_RETURNED_STATUS);
    const [generateBatchPdf] = useMutation<GenerateBatchShippingLabelPdfResponse, GenerateBatchShippingLabelPdfVariables>(GENERATE_BATCH_SHIPPING_LABEL_PDF);
    const [batchMarkForManualReview] = useMutation<BatchMarkForManualReviewResponse, BatchMarkForManualReviewVariables>(BATCH_MARK_FOR_MANUAL_REVIEW);
    const userRole = getUserInfo()?.account?.role?.name || '';
    const [channelOptions, setChannelOptions] = useState<CustomerOption[]>([]);
    const {data} = useQuery<allChannelResponse>(GET_ALL_CHANNEL);
    const {data:exceptionData} = useQuery<ExceptionDictionaryResponse>(GET_ALL_EXCEPTION);
    const exceptionList = exceptionData?.getExceptionDictionaryForHeadquarters || [];
    const [nextActionSelectData, setNextActionSelectData] = useState<NextActionListType[]>([]);
    const {data:nextActionList} = useQuery<NextActionListResponse>(GET_NEXT_ACTION_LIST);
    const { data: hubWarehouseData } = useQuery<WarehousesResponse>(GET_WAREHOUSES_FOR_CENTRAL);
    const { currentWarehouse } = useWarehouseContext();
    const siteType = currentWarehouse?.siteType;
    const exceptionSelectData: ExceptionDictionaryType[] = filterExceptionByUserRole(exceptionList, userRole, siteType);
    
    // 保存选中的 Hub ID，用于级联过滤
    const [selectedHubId, setSelectedHubId] = useState<string | undefined>(undefined);
    
    // 优化的onChange函数，避免不必要的重新渲染
    // 传递 warehouseId，当清空时同时清空 sortingCodeSegment2（避免状态冲突）
    const handleWarehouseIdChange = useCallback((value: string | undefined) => {
        if (!value) {
            // 清空时，同时清空 sortingCodeSegment2 和 warehouseId
            // 必须明确设置为 undefined，这样 updateHashState 会包含这些字段，encodeHash 会过滤它们，URL hash 中不会包含
            setFilters((prev: OrderFiltersType) => ({
                ...prev,
                warehouseId: undefined,
                sortingCodeSegment2: undefined
            }));
        } else {
            // 选择时，只更新 warehouseId，sortingCodeSegment2 由 handleSecondSegmentChange 更新
            setFilters((prev: OrderFiltersType) => ({ 
                ...prev, 
                warehouseId: value
            }));
        }
    }, [setFilters]);

    // 新增：处理2nd Sort Code选择时返回对应的第二段码
    // 注意：清空时不会调用此回调（由 handleWarehouseIdChange 处理），所以这里只处理选择操作
    const handleSecondSegmentChange = useCallback((secondSegment: string | undefined) => {
        // 只在选择时更新 sortingCodeSegment2，清空时由 handleWarehouseIdChange 统一处理
        if (secondSegment) {
            setFilters((prev: OrderFiltersType) => ({ ...prev, sortingCodeSegment2: secondSegment }));
        }
    }, [setFilters]);

    // 新增：处理2nd Sort Code选择时返回对应的Sorting-name
    const handleSortingNameChange = useCallback((sortingName: string | undefined) => {
        // 这里可以根据需要处理Sorting-name，比如存储到filters中或进行其他操作
        // 如果需要将Sorting-name存储到filters中，可以取消下面的注释
        // setFilters((prev: OrderFiltersType) => ({ ...prev, sortingName: sortingName }));
    }, [setFilters]);

    const [generateReportMutation] = useMutation<GenerateParcelDownloadReportResponse, GenerateParcelDownloadReportVariables>(GENERATE_PARCEL_DOWNLOAD_REPORT);
    const [downloadLoading, setDownloadLoading] = useState(false);
    const [cooldownRemaining, setCooldownRemaining] = useState<number>(0);
    const DOWNLOAD_COOLDOWN = 5000; // 5秒冷却时间

    // 下载完成模态框状态
    const [downloadCompleteModalVisible, setDownloadCompleteModalVisible] = useState(false);
    const [generatedFileName, setGeneratedFileName] = useState<string>('');

    // 旧的  使用前端下载Hook
    const {downloadLoading: originalDownloadLoading, downloadParcels} = useParcelDownload();

    // 从localStorage获取上次下载时间，页面刷新后仍然有效
    const getLastDownloadTime = (): number => {
        const stored = localStorage.getItem('parcelDownloadLastTime');
        return stored ? parseInt(stored, 10) : 0;
    };

    const setLastDownloadTime = (time: number) => {
        localStorage.setItem('parcelDownloadLastTime', time.toString());
    };

    const [hubOptions, setHubOptions] = useState<CustomerOption[]>([]);
    const {data:hubOptionsData} = useQuery<GetHubListForOptionResponse>(GET_HUB_LIST_FOR_OPTIONS);
    const { getExceptionReasonText, getNextActionTextFromBackend, exceptionOrderStatusOptions } = useStatusInternationalization();

    // 根据选择的站点（sortingCodeSegment1 和 sortingCodeSegment2）找到对应的 warehouse ID
    const selectedWarehouseId = React.useMemo(() => {
        if (!filters.sortingCodeSegment2 || !hubWarehouseData?.warehouses) {
            return undefined;
        }
        // 查找匹配的 warehouse
        const warehouse = hubWarehouseData.warehouses.find(w => {
            if (w.siteType !== 2) return false; // 只查找 warehouse 类型
            const sortingCode = (w as any).sortingCode as string | undefined;
            const segments = sortingCode?.split('-') || [];
            const firstSegment = segments[0];
            const secondSegment = segments.length >= 2 ? segments[1] : undefined;
            // 匹配第一段和第二段
            return firstSegment === filters.sortingCodeSegment1 && secondSegment === filters.sortingCodeSegment2;
        });
        return warehouse?.id ? Number(warehouse.id) : undefined;
    }, [filters.sortingCodeSegment1, filters.sortingCodeSegment2, hubWarehouseData]);

    // DSP选项
    useEffect(() => {
        if (data?.getAllChannels) {
            const channels = Array.isArray(data.getAllChannels) ? data.getAllChannels : [data.getAllChannels];
            const options = channels.map(channel => ({
                value: channel.id,
                label: channel.channelName
            }));
            setChannelOptions(options);
        }
        if (hubOptionsData) {
            const hubs = Array.isArray(hubOptionsData.allOptions) ? hubOptionsData.allOptions : [hubOptionsData.allOptions];
            setHubOptions(hubs);
        }
        if (nextActionList?.getNextActionList){
            setNextActionSelectData(nextActionList.getNextActionList);
        }
    }, [data,nextActionList,hubOptionsData]);

    // 冷却时间倒计时（页面刷新后仍然有效）
    useEffect(() => {
        const checkCooldown = () => {
            const lastTime = getLastDownloadTime();
            if (lastTime > 0) {
                const currentTime = Date.now();
                const remaining = Math.max(0, DOWNLOAD_COOLDOWN - (currentTime - lastTime));
                const remainingSeconds = Math.ceil(remaining / 1000);
                setCooldownRemaining(remainingSeconds);

                if (remaining <= 0) {
                    setCooldownRemaining(0);
                    setDownloadLoading(false);
                    localStorage.removeItem('parcelDownloadLastTime'); // 清理过期数据
                }
            } else {
                setCooldownRemaining(0);
            }
        };

        // 立即检查一次（页面刷新后）
        checkCooldown();

        // 启动定时器，每秒更新一次
        const interval = setInterval(checkCooldown, 1000);

        return () => clearInterval(interval);
    }, [DOWNLOAD_COOLDOWN]);

    // 处理时间变化的函数
    const handleOrderTimeChange = (
        dates: [string | null, string | null] | null
    ) => {
        hasUserEditedOrderTimeRef.current = true;
        setFilters({...filters, orderTime: dates});
    };

    const handleResetFiltersWithDefaultTime = () => {
        hasUserEditedOrderTimeRef.current = false;
        handleResetFilters();
        if (onClearSelection) onClearSelection();
    };

    const handleHubReceiveChange = (
        dates: [string | null, string | null] | null
    ) => {
        setFilters({...filters, destHubArrivedAt: dates});
    };

    const handleWarehouseReceiveChange = (
        dates: [string | null, string | null] | null
    ) => {
        setFilters({...filters, destWarehouseArrivedAt: dates});
    };

    const handleDeliverTaskChange = (
        dates: [string | null, string | null] | null
    ) => {
        setFilters({...filters, deliverAt: dates});
    };

    const handlePickupAtChange = (
        dates: [string | null, string | null] | null
    ) => {
        setFilters({...filters, receivedAtHub: dates});
    };

    const handleExceptionTimeChange = (
        dates: [string | null, string | null] | null,
    ) => {
        setFilters({...filters, markExceptionAt: dates});
    };

    const handleDeliveredAtChange = (
        dates: [string | null, string | null] | null
    ) => {
        setFilters({...filters, deliveredAt: dates});
    };

    const handleLastDeliveryScanTimeChange = (
        dates: [string | null, string | null] | null
    ) => {
        setFilters({...filters, lastDeliveryScanTime: dates});
    };

    const handleUpdatedAtChange = (
        dates: [string | null, string | null] | null
    ) => {
        setFilters({...filters, updatedAt: dates});
    };

    const handleReturnArrivalHubAtChange = (
        dates: [string | null, string | null] | null
    ) => {
        setFilters({...filters, returnArrivalHubAt: dates});
    };

    const handleBagTaskNumberChange = (value: string | undefined) => {
        setFilters({
            ...filters,
            bagTaskNumbers: value
        });
    };

    const handleMarkAsException = () => {
        if (selectedRows.length < 1) {
            message.warning(t('central.parcels.messages.selectAtLeastOneParcel'));
            return;
        }
        setBatchModalVisible(true);
    };

    const handlePrintLabel = async () => {
        if (selectedRows.length < 1) {
            message.warning(t('central.parcels.messages.selectAtLeastOnePackage'));
            return;
        }

        try {
            // 批量生成一个包含多页的PDF
            const orderIds = selectedRows.map(order => parseInt(order.id));
            const { data } = await generateBatchPdf({
                variables: { orderIds }
            });

            if (data?.generateBatchShippingLabelPdf) {
                // 生成包含所有选中订单号的文件名
                const trackingNumbers = selectedRows
                    .map(order => order.trackingNumber || order.id)
                    .slice(0, 3); // 只取前3个，避免文件名过长
                const filenameSuffix = trackingNumbers.length < selectedRows.length
                    ? `${trackingNumbers.join('_')}_and_${selectedRows.length - trackingNumbers.length}_more`
                    : trackingNumbers.join('_');
                const filename = `shipping_labels_${filenameSuffix}.pdf`;

                printPdfFromBase64(data.generateBatchShippingLabelPdf, filename);
                message.success(t('central.parcels.messages.generatedBatchShippingLabel', { count: selectedRows.length }));
            }
        } catch (error: any) {
            console.error('Print tag failed:', error);
            const graphQLError = error?.graphQLErrors?.[0];
            const messageKey = graphQLError?.extensions?.messageKey;

            if (messageKey === 'errors.business.fbt.sortingOnlyBatchLabelNotSupported') {
                // 针对仅分拣订单错误，格式化 trackingNumbers
                const details = graphQLError.extensions?.details;
                const trackingNumbers = details?.trackingNumbers;
                const formattedDetails = trackingNumbers && Array.isArray(trackingNumbers)
                    ? { ...details, trackingNumbers: trackingNumbers.join(', ') }
                    : details;

                // 使用格式化后的 details 进行翻译并显示
                const errorMessage = String(t(messageKey, formattedDetails));
                message.error(errorMessage);
            } else if (messageKey) {
                // 其他有 messageKey 的错误，使用 showError
                showError(error);
            } else {
                // 兜底：无 messageKey 时使用通用文案
                message.error(t('central.parcels.messages.failedToGenerateBatchLabels'));
            }
        }
    };

    const handleBatchMarkOk = async (orders: BatchMarkExceptionInput[]) => {
        try {
            setBatchMarkLoading(true);
            await Promise.all(orders.map(order =>
                markAsException({
                    variables: {
                        orderId: order.orderId,
                        exceptionReasonCode: order.exceptionReasonCode,
                        exceptionImageS3KeysJson: order.exceptionImageS3KeysJson || [],
                        exceptionHubId: siteType === SiteType.HUB ? currentWarehouse?.id : currentWarehouse?.parentSiteId,
                        exceptionWarehouseId: siteType === SiteType.WAREHOUSE ? currentWarehouse?.id : null
                    },
                    refetchQueries: ['WarehouseQuery']
                })
            ))

           message.success(t('central.parcels.messages.markingExceptionSuccessfully'));
            setBatchModalVisible(false);
            handleFilter();
        } catch (error) {
            console.error('Marking exception failed:', error);
            message.error(t('central.parcels.messages.markingExceptionFailed'));
        } finally {
            setBatchMarkLoading(false);
        }
    };
    const handleSetReturnStatusConfirm = async (formData: SetReturnStatusData) => {
        try {
            setReturnStatusLoading(true);
            await setReturnedStatus({
                variables: {
                    orderIds: formData.orderIds,
                    returnedImageS3KeysJson: formData.returnedImageS3KeysJson
                }
            })
            message.success(t('central.parcels.messages.setReturnStatusSuccessfully'));
            setReturnStatusVisible(false);
            handleFilter();
        } catch (error) {
            console.error('set return status failed:', error);
            const { message: errorMessage } = parseException(error);
            message.error(errorMessage);
        } finally {
            setReturnStatusLoading(false);
        }
    };

    // 构建查询参数 - 与useCentralParcelQuery中的buildQueryVariables保持一致
    const buildQueryFilters = (filters: OrderFiltersType) => {
        const timeRanges = {
            orderTime: formatDateTimeRangeToISO(getEffectiveOrderTime(filters.orderTime)),
            receivedAtHub: formatDateTimeRangeToISO(filters.receivedAtHub),
            destHubArrivedAt: formatDateTimeRangeToISO(filters.destHubArrivedAt),
            destWarehouseArrivedAt: formatDateTimeRangeToISO(filters.destWarehouseArrivedAt),
            deliverAt: formatDateTimeRangeToISO(filters.deliverAt),
            markExceptionAt: formatDateTimeRangeToISO(filters.markExceptionAt),
            deliveredAt: formatDateTimeRangeToISO(filters.deliveredAt),
            lastDeliveryScanTime: formatDateTimeRangeToISO(filters.lastDeliveryScanTime),
            returnArrivalHubAt: formatDateTimeRangeToISO(filters.returnArrivalHubAt)
        };

        // 处理 receiverZipcode：如果有原始输入，转换为数组（与查询列表保持一致）
        let receiverZipcode: string[] | undefined = filters.receiverZipcode;
        if (filters.receiverZipcodeInput) {
            // 支持换行符、半角逗号和空格分隔（多个空格会被视为一个分隔符）
            const zipcodes = filters.receiverZipcodeInput
                .split(/[\r\n,\s]+/)  // 按换行符、逗号或空格分割（\s+ 匹配一个或多个空白字符）
                .map(z => z.trim())
                .filter(z => z.length > 0);
            receiverZipcode = zipcodes.length > 0 ? zipcodes : undefined;
        }

        // 与查询列表保持一致的过滤项构造
        const queryFilters: any = {
            ...filters,
            ...timeRanges,
            // 转换数组类型为多值查询
            channelIds: filters.channelId?.map(id => parseInt(id)) || undefined,
            pickupHubIds: filters.pickupHubId?.map(id => parseInt(id)) || undefined,
            overallStatuses: filters.overallStatus || undefined,
            nextActions: (filters.nextActions && filters.nextActions.length > 0) ? filters.nextActions : undefined,
            deliveredDspIds: (filters.deliveredDspIds && filters.deliveredDspIds.length > 0) ? filters.deliveredDspIds : undefined,
            receiverZipcode: receiverZipcode, // 使用转换后的数组
            pickupTaskDate: undefined, // 不发送到后端
            // 转换 hubId 和 warehouseId 为数字类型（与 useCentralParcelQuery 保持一致）
            hubId: filters.hubId ? parseInt(filters.hubId) : undefined,
            warehouseId: filters.warehouseId ? parseInt(filters.warehouseId) : undefined,
            // 明确保留分拣码段字段，确保筛选条件正确传递
            sortingCodeSegment1: filters.sortingCodeSegment1 || undefined,
            sortingCodeSegment2: filters.sortingCodeSegment2 || undefined,
            sortingCodeSegment3: filters.sortingCodeSegment3 || undefined
        };

        // 清空临时输入字段，不发送到后端
        queryFilters.receiverZipcodeInput = undefined;

        // 清空单值字段，避免与多值字段冲突
        queryFilters.channelId = undefined;
        queryFilters.pickupHubId = undefined;
        queryFilters.overallStatus = undefined;
        queryFilters.nextAction = undefined;
        queryFilters.deliveredDspId = undefined;

        return queryFilters;
    };

    // Download handler function
    const handleDownloadParcels = async () => {
        const exportCount = selectedRows.length > 0 ? selectedRows.length : totalElements;
        if (exportCount > PICKUP_PARCEL_DOWNLOAD_MAX_COUNT) {
            message.warning(`Pickup parcel download exceeds the maximum limit of ${PICKUP_PARCEL_DOWNLOAD_MAX_COUNT} records. Current count: ${exportCount}`);
            return;
        }

        const currentTime = Date.now();
        const lastTime = getLastDownloadTime();

        // Check cooldown time
        if (currentTime - lastTime < DOWNLOAD_COOLDOWN) {
            const remainingTime = Math.ceil((DOWNLOAD_COOLDOWN - (currentTime - lastTime)) / 1000);
            message.warning(`Please wait ${remainingTime} seconds before trying again to avoid duplicate requests`);
            return;
        }

        // Check if already loading
        if (downloadLoading) {
            message.warning('Report is being generated, please do not click repeatedly');
            return;
        }

        if (!currentWarehouse?.id) {
            message.error('Warehouse not ready, please try again later');
            return;
        }

        try {
            setDownloadLoading(true);
            setLastDownloadTime(currentTime); // Store to localStorage

            // 获取用户时区
            const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

            // 使用与filter按钮完全相同的参数构建逻辑
            const queryFilters = buildQueryFilters(filters);

            // 如果有选中的行，只下载选中的数据
            if (selectedRows && selectedRows.length > 0) {
                const trackingNumbers = selectedRows
                    .map(row => row.trackingNumber)
                    .filter((tn): tn is string => !!tn);

                if (trackingNumbers.length > 0) {
                    // 使用选中的 trackingNumbers 覆盖过滤条件
                    queryFilters.trackingNumbers = trackingNumbers.join(';');
                }
            }

            if(filters.trackingNumbers==null || filters.trackingNumbers.trim().length == 0){
                queryFilters.pickupWarehouseId = Number( currentWarehouse?.id)
            }


            const response = await generateReportMutation({
                variables: {
                    filters: queryFilters,
                    timezone: userTimezone,
                    warehouseId: currentWarehouse.id
                }
            });

            const fileName = response.data?.generateParcelDownloadReportForPickup;
            if (fileName) {
                setGeneratedFileName(fileName);
                setDownloadCompleteModalVisible(true);
            } else {
                message.error('Failed to start report generation task');
            }
        } catch (error: any) {
            console.error('Report generation failed:', error);
            // 提取 GraphQL 错误消息
            let errorMessage = 'Report generation failed, please try again later';
            if (error?.graphQLErrors && error.graphQLErrors.length > 0) {
                const graphQLError = error.graphQLErrors[0];
                // 优先使用 extensions.message，如果没有则使用 message
                errorMessage = graphQLError.extensions?.message || graphQLError.message || errorMessage;
            } else if (error?.message) {
                errorMessage = error.message;
            }
            message.error(errorMessage);
            setDownloadLoading(false);
        }
    };

    return (
        <div className="mb-2 pl-0 py-2 [&_.ant-select]:h-8 [&_.ant-select-selector]:h-8 [&_.ant-input:not(textarea)]:h-8 [&_.ant-picker]:h-8">
            <OrderListingQueryHint />
            {/* Base Information */}
            <div className="mb-4">
                <div className="text-sm text-gray-800 font-semibold mb-2">{t('filters.sections.baseInformation')}</div>
                <div className="bg-gray-50 rounded-md px-4 py-2">
                    {/* 使用 Grid 布局确保列对齐（Tracking 占满一行，依赖 gap-y 保持与下一行一致的间距） */}
                    <div className="grid grid-cols-3 gap-x-10 gap-y-4 items-start">
                        <div className="min-w-0 col-span-3">
                            <BatchInputFilterItem
                                label={labelTracking}
                                value={filters.trackingNumbers}
                                onChange={(e: ChangeEvent<HTMLTextAreaElement>) => setFilters({
                                    ...filters,
                                    trackingNumbers: e.target.value || undefined
                                })}
                                placeholder={t('central.parcels.placeholders.trackingNumbers') || ''}
                                spanClassName={col1SpanClassName}
                                rows={2}
                            />
                        </div>
                        <div className="min-w-0">
                <BatchInputFilterItem
                    label={labelOrder}
                    value={filters.channelOrderNumber}
                    onChange={(e: ChangeEvent<HTMLTextAreaElement>) => setFilters({...filters, channelOrderNumber: e.target.value || undefined})}
                    spanClassName={col1SpanClassName}
                    rows={1}
                />
                        </div>
                        <div className="min-w-0">
                <MultiClientSelectFilter
                    label={labelClient}
                    value={filters.channelId}
                    onChange={(value: string[] | undefined) => setFilters({...filters, channelId: value})}
                    spanClassName={getSmartLabelClassName(col2Labels)}
                    placeholder={t('components.input.all') as string}
                />
                        </div>
                        <div className="min-w-0">
                <MultiParcelStatusSelectFilter
                    label={labelParcelStatus}
                    value={filters.overallStatus}
                    onChange={(value: string[] | undefined) => setFilters({...filters, overallStatus: value})}
                    spanClassName={getSmartLabelClassName(col3Labels)}
                    placeholder={t('components.input.all') as string}
                />
                        </div>
                        {/* 第二行 */}
                        <div className="min-w-0">
                <DspSelectFilter
                    label={labelDsp}
                    value={filters.deliveredDspIds?.map(String)}
                    onChange={(value: string[] | undefined) => setFilters({...filters, deliveredDspIds: value?.map(v => parseInt(v)), deliveredDspId: undefined})}
                    spanClassName={col1SpanClassName}
                    placeholder={t('components.input.all') as string}
                    warehouseId={selectedWarehouseId}
                />
                        </div>
                        <div className="min-w-0">
                <InputFilterItem
                    label={labelSortingChannelOrderNumber}
                    value={filters.sortingChannelOrderNumber}
                    onChange={(e) => setFilters({...filters, sortingChannelOrderNumber: e.target.value || undefined})}
                    spanClassName={getSmartLabelClassName(col2Labels)}
                />
                        </div>
                        <div className="min-w-0"></div>
                </div>
                </div>
            </div>

            {/* Route Information */}
            <div className="mb-6">
                <div className="flex items-center justify-between mb-2">
                    <div className="text-sm text-gray-800 font-semibold">{t('filters.sections.routeInformation')}</div>
                </div>
                <div className="bg-gray-50 rounded-md px-4 py-2">
                    {/* 使用 Grid 布局确保列对齐 */}
                    <div className="grid grid-cols-3 gap-x-10 gap-y-4 items-start">
                        <div className="min-w-0">
                <HubSelectFilter
                    label={labelSortCode1}
                    value={filters.sortingCodeSegment1}
                    onChange={(value, hubId) => {
                        // 当清空 Hub 选择时，同时清空相关字段
                        if (!value) {
                            setFilters((prev: OrderFiltersType) => ({
                                ...prev,
                                sortingCodeSegment1: undefined,
                                hubId: undefined,
                                warehouseId: undefined,
                                sortingCodeSegment2: undefined
                            }));
                            setSelectedHubId(undefined);
                        } else {
                            // 选择 Hub 时，同时更新 sortingCodeSegment1 和 hubId，清空 warehouseId 和 sortingCodeSegment2
                            setFilters((prev: OrderFiltersType) => ({
                                ...prev,
                                sortingCodeSegment1: value,
                                hubId: hubId,
                                warehouseId: undefined, // 切换 Hub 时清空 Warehouse
                                sortingCodeSegment2: undefined // 切换 Hub 时清空二段码
                            }));
                            setSelectedHubId(hubId); // 保存选中的 Hub ID，用于级联过滤
                        }
                    }}
                    spanClassName={col1SpanClassName}
                    placeholder={t('central.parcels.placeholders.selectHub') || 'Select HUB'}
                />
                        </div>
                        <div className="min-w-0">
                <WarehouseSelectFilter
                    label={labelSortCode2}
                    value={filters.warehouseId}
                    onChange={handleWarehouseIdChange}
                    onSelectSortingName={handleSortingNameChange}
                    onSelectSecondSegment={handleSecondSegmentChange}
                    hubId={selectedHubId} // 使用 hubId 进行级联过滤（基于 parent_site_id）
                    spanClassName={getSmartLabelClassName(col2Labels)}
                    placeholder={t('central.parcels.placeholders.selectWarehouse') || 'Select Warehouse'}
                />
                        </div>
                        <div className="min-w-0">
                <InputFilterItem
                    label={labelSortCode3}
                    value={filters.sortingCodeSegment3}
                    onChange={(e: ChangeEvent<HTMLInputElement>) => setFilters({...filters, sortingCodeSegment3: e.target.value || undefined})}
                    spanClassName={getSmartLabelClassName(col3Labels)}
                />
                        </div>
                        {/* 第二行 */}
                        <div className="min-w-0">
                <BatchInputFilterItem
                    label={labelReceiverZipcode}
                    value={filters.receiverZipcodeInput || filters.receiverZipcode?.join('\n') || ''}
                    onChange={(e: ChangeEvent<HTMLTextAreaElement>) => {
                        const value = e.target.value;
                        // 与 trackingNumbers 一致：直接保存原始字符串，在查询时转换为数组
                        setFilters({
                            ...filters,
                            receiverZipcodeInput: value || undefined
                        });
                    }}
                    placeholder={t('central.parcels.placeholders.receiverZipcode') || 'Enter multiple zipcodes, separated by line, comma or space'}
                    spanClassName={col1SpanClassName}
                    rows={1}
                />
                        </div>
                        <div className="min-w-0">
                <MultiPickupHubSelectFilter
                    label={labelPickupHub}
                    value={filters.pickupHubId}
                    onChange={(value: string[] | undefined) => setFilters({...filters, pickupHubId: value})}
                    spanClassName={getSmartLabelClassName(col2Labels)}
                    placeholder={t('components.input.all') as string}
                />
                        </div>
                        <div className="min-w-0">
                <InputFilterItem
                    label={labelPickupSite}
                    value={filters.cooperationWarehouseId?.toString()}
                    onChange={(e: ChangeEvent<HTMLInputElement>) => {
                        const value = e.target.value.trim();
                        const numValue = value ? parseInt(value, 10) : undefined;
                        setFilters({
                            ...filters,
                            cooperationWarehouseId: (numValue && !isNaN(numValue)) ? numValue : undefined
                        });
                    }}
                    spanClassName={getSmartLabelClassName(col3Labels)}
                />
                </div>
                        <div className="min-w-0">
                <PrePickupHubSelectFilter
                    label={labelPrePickupHub}
                    value={filters.prePickupHubName}
                    onChange={(name: string | undefined) => {
                        setFilters({ ...filters, prePickupHubName: name });
                    }}
                    spanClassName={col1SpanClassName}
                    placeholder={t('central.parcels.placeholders.prePickupHub') || ''}
                />
                        </div>
                        <div className="min-w-0">
                <BagTaskNumberFilterItem
                    label={labelBagTaskNumber}
                    value={filters.bagTaskNumbers}
                    onChange={handleBagTaskNumberChange}
                    placeholder={t('central.parcels.placeholders.bagTaskNumber') || 'Enter bagging task'}
                    spanClassName={getSmartLabelClassName(col2Labels)}
                />
                        </div>
                        <div className="min-w-0"></div>
                        <div className="min-w-0"></div>
                    </div>
                </div>
            </div>

            {/* Date Information */}
            <div className="mb-6">
                <div className="flex items-center justify-between mb-2">
                    <div className="text-sm text-gray-800 font-semibold">{t('filters.sections.dateInformation')}</div>
                </div>
                <div className="bg-gray-50 rounded-md px-4 py-2">
                    {/* 二分布局 */}
                    <div className="grid grid-cols-2 gap-x-10 gap-y-4 items-start">
                        <div className="min-w-0">
                <DateTimeRangeFilterItem
                    label={labelOrderTime}
                    value={getEffectiveOrderTime(filters.orderTime)}
                    onChange={handleOrderTimeChange}
                    spanClassName={col1SpanClassName}
                />
                        </div>
                        <div className="min-w-0">
                <DateTimeRangeFilterItem
                    label={labelPickupTaskDate}
                    value={filters.receivedAtHub}
                    onChange={handlePickupAtChange}
                    spanClassName={col1SpanClassName}
                />
                        </div>
                        {/* 第二行 */}
                        <div className="min-w-0">
                <DateTimeRangeFilterItem
                    label={labelHubReceiveDate}
                    value={filters.destHubArrivedAt}
                    onChange={handleHubReceiveChange}
                    spanClassName={col1SpanClassName}
                />
                        </div>
                        <div className="min-w-0">
                <DateTimeRangeFilterItem
                    label={labelWhsReceiveDate}
                    value={filters.destWarehouseArrivedAt}
                    onChange={handleWarehouseReceiveChange}
                    spanClassName={col1SpanClassName}
                />
                        </div>
                        {/* 第三行 */}
                        <div className="min-w-0">
                <DateTimeRangeFilterItem
                    label={labelLastTaskDate}
                    value={filters.deliverAt}
                    onChange={handleDeliverTaskChange}
                    spanClassName={col1SpanClassName}
                />
                        </div>
                        <div className="min-w-0">
                <DateTimeRangeFilterItem
                    label={labelDeliveredAt}
                    value={filters.deliveredAt}
                    onChange={handleDeliveredAtChange}
                    spanClassName={col1SpanClassName}
                />
                        </div>
                        {/* 第四行 */}
                        <div className="min-w-0">
                <DateTimeRangeFilterItem
                    label={labelLastDeliveryScanTime}
                    value={filters.lastDeliveryScanTime}
                    onChange={handleLastDeliveryScanTimeChange}
                    spanClassName={col1SpanClassName}
                />
                        </div>
                        <div className="min-w-0">
                <DateTimeRangeFilterItem
                    label={labelLastUpdatedAt}
                    value={filters.updatedAt}
                    onChange={handleUpdatedAtChange}
                    spanClassName={col1SpanClassName}
                />
                        </div>
                        <div className="min-w-0">
                            <DateTimeRangeFilterItem
                                label={labelReturnArrivalHubAt}
                                value={filters.returnArrivalHubAt}
                                onChange={handleReturnArrivalHubAtChange}
                                spanClassName={col1SpanClassName}
                            />
                        </div>
                    </div>
                </div>
            </div>

            {/* Exception Information */}
            <div className="mb-6">
                <div className="flex items-center justify-between mb-2">
                    <div className="text-sm text-gray-800 font-semibold">{t('filters.sections.exceptionInformation')}</div>
                </div>
                <div className="bg-gray-50 rounded-md px-4 py-2">
                    {/* 使用 Grid 布局确保列对齐 */}
                    <div className="grid grid-cols-3 gap-x-10 gap-y-4 items-start">
                        <div className="min-w-0">
                <ParcelFilterStyle
                    label={labelIsComplaint}
                    options={isComplaintOptions}
                    value={filters.isComplaint?.toString() ?? undefined}
                    onChange={(value: string) => setFilters({...filters, isComplaint: value ? (value === 'true') : undefined})}
                    spanClassName={col1SpanClassName}
                />
                        </div>
                        <div className="min-w-0">
                <ParcelFilterStyle
                    label={labelPodReviewStatus}
                    options={podReviewStatusOptions}
                    value={filters.podReviewStatus ?? undefined}
                    onChange={(value: string) => setFilters({...filters, podReviewStatus: value || undefined})}
                    spanClassName={getSmartLabelClassName(col2Labels)}
                />
                        </div>
                        <div className="min-w-0">
                <ParcelFilterStyle
                    label={labelHasException}
                    options={hasExceptionOptions}
                    value={filters.isHasBeenSetException?.toString() ?? undefined}
                    onChange={(value: string) => setFilters({...filters, isHasBeenSetException: value ? (value === 'true') : undefined})}
                    spanClassName={getSmartLabelClassName(col3Labels)}
                />
                        </div>
                        {/* 第二行 */}
                        <div className="min-w-0">
                <ParcelFilterStyle
                    label={labelLastExceptionReason}
                    options={exceptionList.map(ex => ({
                        value: ex.exceptionReasonCode,
                        label: getExceptionReasonText(ex, ex.exceptionReasonCode)
                    })) || []}
                    value={filters.exceptionReasonCode ?? undefined}
                    onChange={(value: string) => setFilters({...filters, exceptionReasonCode: value || undefined})}
                    spanClassName={col1SpanClassName}
                />
                        </div>
                        <div className="min-w-0">
                <ParcelFilterStyle
                    label={labelExceptionStatus}
                    options={exceptionOrderStatusOptions}
                    mode="multiple"
                    maxTagCount="responsive"
                    value={filters?.exceptionStatuses?.map(String) ?? []}
                    onChange={(values: string[]) => setFilters({...filters, exceptionStatuses: values && values.length > 0 && values.length < exceptionOrderStatusOptions.length ? values.map(Number) : undefined})}
                    spanClassName={getSmartLabelClassName(col2Labels)}
                />
                        </div>
                        <div className="min-w-0">
                <ParcelFilterStyle
                    label={labelNextAction}
                    options={nextActionSelectData.filter(item => item.value !== 0).map(item => ({
                        value: item.name,
                        label: getNextActionTextFromBackend(item.name)
                    }))}
                    mode="multiple"
                    value={filters.nextActions}
                    onChange={(value: string[] | undefined) => setFilters({...filters, nextActions: value, nextAction: undefined})}
                    spanClassName={getSmartLabelClassName(col3Labels)}
                />
                        </div>
                    </div>

                    {/* 第三行（二分）：上次异常时间 */}
                    <div className="grid grid-cols-2 gap-x-10 gap-y-4 items-start mt-4">
                        <div className="min-w-0">
                            <DateTimeRangeFilterItem
                                label={labelLastExceptionTime}
                                value={filters.markExceptionAt}
                                onChange={handleExceptionTimeChange}
                                spanClassName={col1SpanClassName}
                                format="YYYY/MM/DD HH:mm"
                                showTime={{
                                    format: 'HH:mm',
                                    defaultValue: [dayjs('00:00', 'HH:mm'), dayjs('23:59', 'HH:mm')],
                                }}
                            />
                        </div>
                        <div className="min-w-0"></div>
                    </div>
                </div>
            </div>

            <div className="mb-4">
                <div className="flex flex-wrap gap-x-2 gap-y-4">
                    <div className="flex flex-wrap gap-2">
                        <Button 
                            type="primary" 
                            onClick={handlePrintLabel}
                            className="w-auto min-w-24"
                        >
                            <span className="font-bold">{t('central.parcels.buttons.printLabel')}</span>
                        </Button>
                        <Button 
                            type="primary" 
                            onClick={handleMarkAsException}
                            className="w-auto min-w-24"
                        >
                            <span className="font-bold">{t('central.parcels.buttons.markAsException')}</span>
                        </Button>
                    </div>

                    <div className="flex flex-wrap gap-2 ml-auto">
                    <Button
                        type="primary"
                        onClick={handleDownloadParcels}
                        loading={cooldownRemaining > 0}
                        disabled={downloadLoading || cooldownRemaining > 0}
                            className="w-auto min-w-24"
                    >
                            <span className="font-bold">
                        {cooldownRemaining > 0
                            ? `Download ${cooldownRemaining}s`
                                : t('components.button.download')
                        }
                            </span>
                    </Button>
                        <Button 
                            type="primary" 
                            onClick={handleFilter}
                            className="w-auto min-w-24"
                        >
                            <span className="font-bold">{t('components.button.filter')}</span>
                    </Button>
                        <Button 
                            onClick={handleResetFiltersWithDefaultTime}
                            className="w-auto min-w-24"
                        >
                            <span className="font-bold">{t('components.button.reset')}</span>
                    </Button>
                    </div>
                </div>
            </div>

            {/* 批量标记异常弹窗 */}
            <BatchMarkAsExceptionModal
                    visible={batchModalVisible}
                    parcels={selectedRows.map(row => ({
                        orderId: Number(row.id), 
                        trackingNumber: row.trackingNumber,
                        existingImageUrls: row.exceptionImageS3UrlsJson || [],
                        exceptionHubId: row.exceptionHubId,
                        exceptionWarehouseId: row.exceptionWarehouseId
                    }))}
                    onOk={handleBatchMarkOk}
                    onCancel={() => setBatchModalVisible(false)}
                    loading={batchMarkLoading}
                    userRole={''}
                    exceptionSelectData={exceptionSelectData}
                />
                
            <SetReturnStatusModal
                visible={returnStatusVisible}
                onClose={() => setReturnStatusVisible(false)}
                onConfirm={handleSetReturnStatusConfirm}
                loading={returnStatusLoading}
                parcels={selectedRows}
            />
            <ParcelsDownloadModal
                visible={downloadCompleteModalVisible}
                onClose={() => setDownloadCompleteModalVisible(false)}
                fileName={generatedFileName}
            />

            <AddToPodReviewModal
                visible={addToPodReviewModalVisible}
                onClose={() => setAddToPodReviewModalVisible(false)}
                result={addToPodReviewResult}
            />
        </div>
    );
};

export default CentralParcelFilter;
